import { defineStore } from "pinia";
import { store } from "../index";
import { asyncRouterMap, constantRouterMap } from "@/router";
import {
  generateRoutesByFrontEnd,
  // flatMultiLevelRoutes
} from "@/utils/routerHelper";
import { cloneDeep } from "lodash-es";

// 菜单编码到国际化键的映射
const menuCodeToTitleKey: Record<string, string> = {
  platform: "router.platform",
  dashboard: "router.dashboard",
  operation: "router.operation",
  report: "router.report",
  settings: "router.settings",
  system: "router.system",
  "agency-management": "router.agencyManagement",
  "machine-management": "router.machineManagement",
  "sales-analysis": "router.salesAnalysis",
  "transaction-report": "router.transactionReport",
  "my-account": "router.myAccount",
  "user-management": "router.userManagement",
  "role-management": "router.roleManagement",
  "system-user-management": "router.systemUserManagement",
  "country-management": "router.countryManagement",
  "currency-management": "router.currencyManagement",
  "device-management": "router.deviceManagement",
  "device-tag-management": "router.deviceTagManagement",
  "device-type-management": "router.deviceTypeManagement",
  "dictionary-management": "router.dictionaryManagement",
  "machine-type-management": "router.machineTypeManagement",
  "media-resource-management": "router.mediaResourceManagement",
  "menu-management": "router.menuManagement",
  "payment-method-management": "router.paymentMethodManagement",
  "payment-provider-management": "router.paymentProviderManagement",
  "permission-group-management": "router.permissionGroupManagement",
  "qrcode-sticker-management": "router.qrcodeStickerManagement",
  "system-parameter-management": "router.systemParameterManagement",
  "org-tag-management": "router.orgTagManagement",
  "ota-management": "router.otaManagement",
  "business-intelligence": "router.businessIntelligence",
  "qrCodeTransferLog": "router.qrCodeTransferLog",
  "deviceTransferLog": "router.deviceTransferLog",
};

// 菜单编码到默认图标的映射（基于数据库中的实际图标）
const menuCodeToIcon: Record<string, string> = {
  dashboard: "el-icon-s-data",
  operation: "el-icon-s-operation",
  report: "el-icon-s-marketing",
  settings: "el-icon-s-tools",
  system: "el-icon-s-platform",
  "agency-management": "el-icon-user",
  "machine-management": "el-icon-cpu",
  "sales-analysis": "el-icon-s-data",
  "transaction-report": "el-icon-s-finance",
  "my-account": "el-icon-user-solid",
  "user-management": "el-icon-s-custom",
  "role-management": "el-icon-s-check",
  "system-user-management": "el-icon-s-custom",
  "country-management": "el-icon-location",
  "currency-management": "el-icon-coin",
  "device-management": "el-icon-cpu",
  "device-tag-management": "el-icon-price-tag",
  "device-type-management": "el-icon-s-platform",
  "dictionary-management": "el-icon-s-order",
  "machine-type-management": "el-icon-s-platform",
  "media-resource-management": "el-icon-picture",
  "menu-management": "el-icon-s-grid",
  "payment-method-management": "el-icon-credit-card",
  "payment-provider-management": "el-icon-bank-card",
  "permission-group-management": "el-icon-s-check",
  "qrcode-sticker-management": "el-icon-picture-outline",
  "system-parameter-management": "el-icon-s-tools",
  "org-tag-management": "el-icon-price-tag",
  "business-intelligence": "ep:data-board",
};

// 获取菜单标题的国际化键
function getMenuTitleKey(menuCode: string): string {
  return menuCodeToTitleKey[menuCode] || `router.${menuCode}`;
}

// 获取菜单的默认图标
function getDefaultIcon(menuCode: string): string {
  return menuCodeToIcon[menuCode] || "el-icon-menu";
}

// 将后端菜单数据转换为mock格式的路由
export function convertBackendMenuToRoutes(menuData: any[]) {
  if (!menuData || !Array.isArray(menuData)) {
    return [];
  }

  // 不过滤隐藏的菜单项，因为子菜单可能需要父菜单的引用
  const allMenus = menuData;
  const hasChildMenu = (menuId: number): boolean =>
    allMenus.some((item) => item.sid === menuId);

  // 递归构建路由树
  const buildRouteTree = (parentId: number): AppRouteRecordRaw[] => {
    const children = allMenus.filter((item) => item.sid === parentId);

    return children.map((child) => {
      // 构建路由名称
      const name =
        child.menu_code.charAt(0).toUpperCase() +
        child.menu_code
          .slice(1)
          .replace(/-([a-z])/g, (g) => g[1].toUpperCase());

      const route: AppRouteRecordRaw = {
        path: child.menu_code, // 子路由使用相对路径
        name,
        meta: {
          menuCode: child.menu_code,
          title: getMenuTitleKey(child.menu_code),
          icon: child.menu_icon || getDefaultIcon(child.menu_code),
          alwaysShow: child.menu_url === "0",
          menu_url: child.menu_url,
          hidden: child.hidden === "true", // 添加隐藏标记
        },
      };

      // 设置组件
      if (child.menu_url === "0") {
        // 父菜单
        if (parentId === 1) {
          // 顶级父菜单使用Layout
          route.component = () => import("@/layout/Layout.vue");
        } else {
          // 中间层级父菜单使用AppView
          route.component = () => import("@/layout/components/AppView.vue");
        }
      } else {
        // 页面菜单 - 动态导入
        // 使用 Vite 的 import.meta.glob 来动态导入所有组件
        const modules = import.meta.glob("@/views/**/*.vue");

        // 构建组件路径 - 使用 /src/views/ 格式来匹配 import.meta.glob 的返回格式
        const componentPath = `/src/views/${child.menu_url}.vue`;

        // 查找匹配的组件
        const componentModule = modules[componentPath];

        if (componentModule) {
          route.component = componentModule;
        } else {
          console.warn(`Component not found: ${componentPath}`);
          // 如果找不到组件，使用 404 组件
          route.component = () => import("@/views/Error/404.vue");
        }
      }

      // 递归构建子路由
      const childRoutes = buildRouteTree(child.id);
      if (childRoutes.length > 0) {
        route.children = childRoutes;
      }

      return route;
    });
  };

  // 从顶级菜单开始构建（sid=1的菜单，排除sid=0的隐藏根节点）
  const topLevelMenus = allMenus.filter((item) => {
    if (item.sid !== 1) {
      return false;
    }

    if (item.menu_url === "0" && !hasChildMenu(item.id)) {
      return false;
    }

    return true;
  });
  const result = topLevelMenus.map((menu) => {
    const name =
      menu.menu_code.charAt(0).toUpperCase() +
      menu.menu_code.slice(1).replace(/-([a-z])/g, (g) => g[1].toUpperCase());

    const route: AppRouteRecordRaw = {
      path: `/${menu.menu_code}`,
      name,
      meta: {
        menuCode: menu.menu_code,
        title: getMenuTitleKey(menu.menu_code),
        icon: menu.menu_icon || getDefaultIcon(menu.menu_code),
        alwaysShow: menu.menu_url === "0",
        menu_url: menu.menu_url,
        hidden: menu.hidden === "true", // 添加隐藏标记
      },
    };

    // 设置组件 - 所有顶级菜单都使用Layout
    route.component = () => import("@/layout/Layout.vue");

    // 如果有具体的页面URL，添加为子路由
    if (menu.menu_url !== "0") {
      const modules = import.meta.glob("@/views/**/*.vue");
      const componentPath = `/src/views/${menu.menu_url}.vue`;
      const componentModule = modules[componentPath];

      //

      if (componentModule) {
        route.children = [
          {
            path: "",
            name: `${name}Index`,
            component: componentModule,
            meta: {
              menuCode: menu.menu_code,
              title: getMenuTitleKey(menu.menu_code),
              icon: menu.menu_icon || getDefaultIcon(menu.menu_code),
              hidden: false,
            },
          },
        ];

        // 仅在生成阶段打印一次
      } else {
        console.warn(`Component not found: ${componentPath}`);
        route.children = [
          {
            path: "",
            name: `${name}Index`,
            component: () => import("@/views/Error/404.vue"),
            meta: {
              menuCode: menu.menu_code,
              title: getMenuTitleKey(menu.menu_code),
              icon: menu.menu_icon || getDefaultIcon(menu.menu_code),
              hidden: false,
            },
          },
        ];
      }
    }

    const children = buildRouteTree(menu.id);
    if (children.length > 0) {
      // 合并子路由
      if (route.children) {
        route.children = route.children.concat(children);
      } else {
        route.children = children;
      }
    }

    return route;
  });

  return result;
}

export interface PermissionState {
  routers: AppRouteRecordRaw[];
  addRouters: AppRouteRecordRaw[];
  isAddRouters: boolean;
  menuTabRouters: AppRouteRecordRaw[];
}

export const usePermissionStore = defineStore("permission", {
  state: (): PermissionState => ({
    routers: [],
    addRouters: [],
    isAddRouters: false,
    menuTabRouters: [],
  }),
  getters: {
    getRouters(): AppRouteRecordRaw[] {
      return this.routers;
    },
    getAddRouters(): AppRouteRecordRaw[] {
      // 使用原始路由结构，避免扁平化导致的问题
      return cloneDeep(this.addRouters);
    },
    getIsAddRouters(): boolean {
      return this.isAddRouters;
    },
    getMenuTabRouters(): AppRouteRecordRaw[] {
      return this.menuTabRouters;
    },
  },
  actions: {
    generateRoutes(
      type: "server" | "frontEnd" | "static",
      routers?: AppCustomRouteRecordRaw[] | string[],
    ): Promise<unknown> {
      return new Promise<void>((resolve) => {
        let routerMap: AppRouteRecordRaw[] = [];
        if (type === "server") {
          // 将后端菜单数据转换为mock格式
          routerMap = convertBackendMenuToRoutes(
            routers as any[],
          ) as AppRouteRecordRaw[];
        } else if (type === "frontEnd") {
          // 模拟前端过滤菜单
          routerMap = generateRoutesByFrontEnd(
            cloneDeep(asyncRouterMap),
            routers as string[],
          );
        } else {
          // 直接读取静态路由表
          routerMap = cloneDeep(asyncRouterMap);
        }
        // 动态路由，404一定要放到最后面
        this.addRouters = routerMap.concat([
          {
            path: "/:path(.*)*",
            redirect: "/404",
            name: "404Page",
            meta: {
              hidden: true,
              breadcrumb: false,
            },
          },
        ]);
        // 渲染菜单的所有路由
        this.routers = cloneDeep(constantRouterMap).concat(routerMap);
        resolve();
      });
    },
    setIsAddRouters(state: boolean): void {
      this.isAddRouters = state;
    },
    setMenuTabRouters(routers: AppRouteRecordRaw[]): void {
      this.menuTabRouters = routers;
    },
  },
  persist: {
    paths: ["isAddRouters"],
  },
});

export const usePermissionStoreWithOut = () => {
  return usePermissionStore(store);
};
